import React, { useState, useEffect, useRef } from 'react';
import { supabaseClient } from '../../services/supabase';

const GANTT_START_HOUR = 11;
const GANTT_END_HOUR = 22;
const GANTT_SLOT_MINUTES = 15;
const GANTT_RESERVATION_MINUTES = 90;
const GANTT_TOTAL_HOURS = GANTT_END_HOUR - GANTT_START_HOUR + 1;
const GANTT_BOUNDARY_COUNT = GANTT_TOTAL_HOURS + 1;
const GANTT_SLOTS_PER_RESERVATION = GANTT_RESERVATION_MINUTES / GANTT_SLOT_MINUTES;

function GanttChart({ date }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const ganttChartRef = useRef(null);

  useEffect(() => {
    loadGanttData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (!loading && ganttChartRef.current) {
      renderGanttGrid();
      renderGanttAxis();
    }
  }, [loading, reservations]);

  const normalizeTimeString = (timeStr) => {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return timeStr;
    const hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    const normalizedMinute = Math.floor(minute / GANTT_SLOT_MINUTES) * GANTT_SLOT_MINUTES;
    return `${String(hour).padStart(2, '0')}:${String(normalizedMinute).padStart(2, '0')}`;
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = GANTT_START_HOUR; hour <= GANTT_END_HOUR; hour++) {
      for (let minute = 0; minute < 60; minute += GANTT_SLOT_MINUTES) {
        const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  };

  const createGanttBlock = (reservation, timeSlots) => {
    const normalizedTime = normalizeTimeString(reservation.reservation_time);
    const startIndex = timeSlots.indexOf(normalizedTime);
    if (startIndex === -1) return null;

    const totalSlots = timeSlots.length;
    const leftPercent = (startIndex / totalSlots) * 100;
    const widthPercent = (GANTT_SLOTS_PER_RESERVATION / totalSlots) * 100;

    return {
      left: leftPercent,
      width: widthPercent,
      reservation: reservation,
    };
  };

  const buildGanttRow = (label, reservations, timeSlots) => {
    const blocks = (reservations || [])
      .map((reservation) => createGanttBlock(reservation, timeSlots))
      .filter((block) => block !== null);

    return { label, blocks };
  };

  const loadGanttData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from('reservations')
        .select('*')
        .eq('reservation_date', date)
        .in('status', ['confirmed', 'pending'])
        .order('reservation_time', { ascending: true });

      if (error) throw error;

      const seats = {
        counter: [],
        table: [],
      };

      (data || []).forEach((reservation) => {
        seats[reservation.seat_type].push({
          ...reservation,
          reservation_time: normalizeTimeString(reservation.reservation_time),
        });
      });

      setReservations(seats);
    } catch (error) {
      console.error('ガントチャートデータ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderGanttGrid = () => {
    if (!ganttChartRef.current) return;
    const ganttGrid = ganttChartRef.current.querySelector('#ganttGrid');
    if (!ganttGrid) return;

    // 30分単位のグリッド数（1時間あたり2つ）
    const halfHourCount = GANTT_TOTAL_HOURS * 2;
    ganttChartRef.current.style.setProperty('--gantt-half-hour-count', halfHourCount);
    ganttGrid.innerHTML = '';

    for (let i = 0; i <= halfHourCount; i++) {
      const line = document.createElement('div');
      const isHour = i % 2 === 0; // 偶数は1時間単位
      const hour = GANTT_START_HOUR + Math.floor(i / 2);
      const minute = (i % 2) * 30;
      
      line.className = isHour ? 'gantt-line-hour' : 'gantt-line-half';
      line.title = `${hour}:${String(minute).padStart(2, '0')}`;
      line.style.left = `${(i / halfHourCount) * 100}%`;
      ganttGrid.appendChild(line);
    }
  };

  const renderGanttAxis = () => {
    if (!ganttChartRef.current) return;
    const axis = ganttChartRef.current.querySelector('#ganttAxis');
    if (!axis) return;
    axis.innerHTML = '';

    const segments = Math.max(GANTT_BOUNDARY_COUNT - 1, 1);
    for (let i = 0; i < GANTT_BOUNDARY_COUNT; i++) {
      const percent = (i / segments) * 100;
      const label = document.createElement('div');
      label.className = 'gantt-axis-label';
      label.style.left = `${percent}%`;
      label.textContent = `${String(GANTT_START_HOUR + i).padStart(2, '0')}:00`;
      axis.appendChild(label);
    }
  };

  if (loading) {
    return (
      <div className="gantt-chart">
        <p style={{ color: '#ffffff', textAlign: 'center' }}>読み込み中...</p>
      </div>
    );
  }

  const timeSlots = generateTimeSlots();
  const rows = [];

  // カウンター席は1〜7で行分け表示（大人2名以上のカウンター予約は人数分のブロックを展開）
  const expandedCounter = [];
  (reservations.counter || []).forEach((r) => {
    const count = Math.max(1, parseInt(r.adult_count, 10) || 1);
    for (let n = 0; n < count; n++) {
      expandedCounter.push(r);
    }
  });
  for (let i = 1; i <= 7; i++) {
    const counterReservations = expandedCounter.filter((_, idx) => idx % 7 === i - 1);
    rows.push(buildGanttRow(`カウンター席${i}`, counterReservations, timeSlots));
  }

  // テーブル席
  for (let i = 1; i <= 3; i++) {
    const tableReservations = (reservations.table || []).filter((r, idx) => idx % 3 === i - 1);
    rows.push(buildGanttRow(`テーブル席${i}`, tableReservations, timeSlots));
  }

  return (
    <div className="gantt-chart" ref={ganttChartRef}>
      <div className="gantt-rows-container">
        <div className="gantt-grid" id="ganttGrid"></div>
        <div className="gantt-rows" id="ganttContent">
          {rows.map((row, idx) => (
            <div key={idx} className="gantt-row">
              <div className="gantt-label">{row.label}</div>
              <div className="gantt-timeline">
                {row.blocks.map((block, blockIdx) => (
                  <div
                    key={blockIdx}
                    className="gantt-block"
                    style={{
                      left: `${block.left}%`,
                      width: `${block.width}%`,
                    }}
                    title={`${block.reservation.customer_name || '（削除済み）'} (${block.reservation.reservation_time})`}
                  >
                    {block.reservation.customer_name ? `${block.reservation.customer_name} 様` : '（削除済み）'}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="gantt-axis" id="ganttAxis"></div>
    </div>
  );
}

export default GanttChart;

